import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, idOf, nextDocNo } from './c07.shared'
import { batchOfQr } from './inventoryStock'
import {
  batchNoOf,
  bigQty,
  binCode,
  dayDiff,
  discOf,
  dsrFlow,
  dueEndOfDay,
  hasRole,
  iso,
  KADIV_LABEL,
  labelLocation,
  logOf,
  materialCode,
  materialName,
  movement,
  num,
  type OpnLog,
  operatorsOf,
  parseWh,
  perBigOf,
  ROLE,
  requireWhRole,
  round3,
  skuCode,
  skuName,
  statusCodeOf,
  systemTransition,
  TERMINAL_DSR,
  txLogWh,
  up,
  userName,
  type Wh,
  warehouseId,
  whOf,
  wibStamp,
  withLog
} from './opname.shared'
import { dscWindow, slaEnabled } from './slaSetting'

/**
 * C13 — Discrepancy (WF-DSR-01, entitas `discrepancy`): papan OPN-04, detail OPN-04A, konfirmasi "Ditemukan" OPN-03.
 * Eskalasi otomatis lewat `sweepDiscrepancies()` (dipanggil setiap baca) — pengganti worker SLA_DSR_* TL 12B.
 * Write-off yang disetujui Kadiv menulis penyesuaian stok resmi (`stock_movement` OPN_WRITE_OFF + `tx_log`).
 */

export const DISC_TYPE_LABEL: Record<string, string> = {
  MISSING: 'Selisih Opname (hilang)',
  QTY_LESS: 'Selisih Jumlah Tumpukan (kurang)',
  QTY_MORE: 'Selisih Jumlah Tumpukan (lebih)',
  UNKNOWN: 'Temuan Tak Dikenal',
  PACK_LESS: 'Selisih Qty Pack (kurang)',
  PACK_MORE: 'Selisih Qty Pack (lebih)'
}
const STATUS_LABEL: Record<string, string> = {
  DSR_INVESTIGATION: 'Open',
  DSR_PENDING_APP: 'Menunggu Approval',
  DSR_RESOLVED_FOUND: 'Selesai — Ditemukan',
  DSR_ADJUSTED_WRITE_OFF: 'Selesai — Write-off'
}

const actor = (wh: Wh, kind: 'ADMIN' | 'OP' | 'KADIV') => [`ACT_DSR_${kind}_${wh}`]

/* ============================================================ sweep eskalasi */

export function sweepDiscrepancies(now = Date.now()) {
  const escalated: string[] = []
  readTable('discrepancy')
    .filter(disc => statusCodeOf(disc) === 'DSR_INVESTIGATION' && disc.window_due_at)
    .filter(disc => now > Date.parse(String(disc.window_due_at)))
    /* Aturan dimatikan di Master SLA (`DSC_ESCALATE_RM/FG`) → selisih menunggu keputusan manual. */
    .filter(disc => slaEnabled(`DSC_ESCALATE_${whOf(disc.warehouse_id)}`))
    .forEach(disc => {
      const wh = whOf(disc.warehouse_id)
      systemTransition(
        'WF-DSR-01',
        disc,
        'ACT_DSR_AUTO_ESCALATE',
        `Window investigasi H+${dscWindow(wh)[0]} lewat — dieskalasi otomatis ke ${KADIV_LABEL[wh]}`
      )
      updateRow('discrepancy', String(disc.id), {
        escalated_at: iso(),
        log: withLog(
          disc,
          `Window investigasi H+${dscWindow(wh)[0]} lewat — dieskalasi otomatis ke ${KADIV_LABEL[wh]}`,
          null
        )
      })
      escalated.push(String(disc.dsc_no))
    })
  return { escalated }
}

/* ============================================================ pembentukan */

type CreateSpec = {
  wh: Wh
  source: 'OPNAME' | 'PICKING'
  type: keyof typeof DISC_TYPE_LABEL
  qty: number
  unitName: string
  location: string
  code: string
  trace: string
  unit?: LocalRow
  label?: LocalRow
  packId?: string | null
  skuId?: string | null
  materialId?: string | null
  sessionId?: string | null
  taskId?: string | null
  qtySystem?: number | null
  qtyPhysical?: number | null
  event: string
}

export function createDiscrepancy(spec: CreateSpec) {
  const id = newId()
  const { statusId, submissionId } = dsrFlow.start(id)
  const now = iso()
  const [escalate, approval] = dscWindow(spec.wh)
  return insertRow('discrepancy', {
    ...auditFieldsForCreate(),
    id,
    dsc_no: nextDocNo('DSC', 'discrepancy', 'dsc_no'),
    unit_id: spec.unit?.id ?? null,
    label_id: spec.label?.id ?? null,
    transfer_id: null,
    source_type: spec.source,
    qty: round3(spec.qty),
    rack_bin_id: spec.unit?.rack_bin_id ?? null,
    warehouse_zone_id: spec.unit?.warehouse_zone_id ?? null,
    disc_type: spec.type,
    status_id: statusId,
    submission_id: submissionId,
    window_due_at: dueEndOfDay(now, escalate),
    escalated_at: null,
    approver_id: null,
    note: null,
    session_id: spec.sessionId ?? null,
    warehouse_id: warehouseId(spec.wh),
    task_id: spec.taskId ?? null,
    material_id: spec.materialId ?? spec.unit?.material_id ?? null,
    sku_id: spec.skuId ?? spec.label?.sku_id ?? null,
    pack_stock_id: spec.packId ?? null,
    location_code: spec.location,
    scanned_code: spec.code,
    unit_name: spec.unitName,
    qty_system: spec.qtySystem ?? null,
    qty_physical: spec.qtyPhysical ?? null,
    trace: spec.trace,
    approval_due_at: dueEndOfDay(now, approval),
    recheck_operator_id: null,
    recheck_note: null,
    resolved_at: null,
    resolved_by_id: null,
    approved_at: null,
    reject_reason: null,
    parent_id: null,
    found_qty: null,
    log: [{ at: wibStamp(), ev: spec.event, by: currentUserId() }]
  })
}

/** Unit / label yang "Tandai Tidak Ditemukan" saat picking → selisih Open (BA 00 WF-005 / WF-011). */
export function createPickingDiscrepancy(input: { unit?: LocalRow; label?: LocalRow; taskNo: string; page: string }) {
  if (input.unit) {
    const unit = input.unit
    const disc = createDiscrepancy({
      wh: 'RM',
      source: 'PICKING',
      type: 'MISSING',
      qty: bigQty(unit),
      unitName: String(unit.unit_big_snapshot ?? ''),
      location: binCode(unit.rack_bin_id),
      code: String(unit.qr_code),
      unit,
      trace: `Tidak ditemukan saat picking ${input.taskNo}`,
      event: `Tidak ditemukan saat picking ${input.taskNo} — selisih dicatat untuk opname`
    })
    txLogWh(
      'RM',
      input.page,
      'disc-picking',
      String(unit.qr_code),
      `Selisih ${String(disc.dsc_no)} dari picking ${input.taskNo}`
    )
    return disc
  }
  const label = input.label as LocalRow
  const disc = createDiscrepancy({
    wh: 'FG',
    source: 'PICKING',
    type: 'MISSING',
    qty: 1,
    unitName: String(label.level ?? ''),
    location: labelLocation(label),
    code: String(label.label_no),
    label,
    trace: `Tidak ditemukan saat picking ${input.taskNo}`,
    event: `Tidak ditemukan saat picking ${input.taskNo} — selisih dicatat untuk opname`
  })
  txLogWh(
    'FG',
    input.page,
    'disc-picking',
    String(label.label_no),
    `Selisih ${String(disc.dsc_no)} dari picking ${input.taskNo}`
  )
  return disc
}

/* ============================================================ baca */

const itemName = (disc: LocalRow) =>
  disc.material_id ? materialName(disc.material_id) : disc.sku_id ? skuName(disc.sku_id) : ''
const itemCode = (disc: LocalRow) =>
  disc.material_id ? materialCode(disc.material_id) : disc.sku_id ? skuCode(disc.sku_id) : ''

function windowText(disc: LocalRow, now = Date.now()) {
  const code = statusCodeOf(disc)
  if (code === 'DSR_INVESTIGATION') {
    const left = dayDiff(now, String(disc.window_due_at))
    return { text: left > 0 ? `sisa ${left} hari sebelum eskalasi` : 'eskalasi hari ini', late: false }
  }
  if (code === 'DSR_PENDING_APP') {
    const left = dayDiff(now, String(disc.approval_due_at))
    return left < 0
      ? { text: `lewat target approval ${-left} hari`, late: true }
      : { text: left === 0 ? 'target approval hari ini' : `target approval ${left} hari lagi`, late: false }
  }
  if (code === 'DSR_ADJUSTED_WRITE_OFF') return { text: `disetujui ${userName(disc.approver_id)}`, late: false }
  return { text: `ditemukan ${userName(disc.resolved_by_id)}`, late: false }
}

export function discRow(disc: LocalRow) {
  const code = statusCodeOf(disc)
  const session = disc.session_id ? findRow('opname_session', idOf(disc.session_id)) : undefined
  const window = windowText(disc)
  return {
    id: String(disc.id),
    dsc_no: String(disc.dsc_no),
    warehouse: whOf(disc.warehouse_id),
    session_id: session ? String(session.id) : null,
    session_no: session ? String(session.opn_no) : null,
    source_type: String(disc.source_type),
    source_label: disc.source_type === 'PICKING' ? 'temuan dini saat picking' : 'sesi opname',
    code: String(disc.scanned_code ?? ''),
    item_code: itemCode(disc),
    item_name: itemName(disc),
    qty: num(disc.qty),
    unit_name: String(disc.unit_name ?? ''),
    disc_type: String(disc.disc_type),
    type_label: DISC_TYPE_LABEL[String(disc.disc_type)] ?? String(disc.disc_type),
    location: String(disc.location_code ?? ''),
    trace: String(disc.trace ?? ''),
    status: code,
    status_label: STATUS_LABEL[code] ?? code,
    window_text: window.text,
    window_late: window.late,
    created_at: String(disc.date_created),
    recheck_operator: disc.recheck_operator_id ? userName(disc.recheck_operator_id) : null,
    recheck_operator_id: disc.recheck_operator_id ? String(disc.recheck_operator_id) : null,
    is_pack: Boolean(disc.pack_stock_id)
  }
}

export function discrepancies(params: { wh?: string; session?: string; status?: string } | undefined) {
  sweepDiscrepancies()
  const wh = parseWh(params?.wh)
  const rows = readTable('discrepancy')
    .filter(disc => whOf(disc.warehouse_id) === wh)
    .filter(disc => !params?.session || idOf(disc.session_id) === params.session)
    .map(discRow)
    .filter(row => !params?.status || row.status === params.status)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
  const count = (target: Wh, code: string) =>
    readTable('discrepancy').filter(disc => whOf(disc.warehouse_id) === target && statusCodeOf(disc) === code).length
  return {
    wh,
    rows,
    pending: rows.filter(row => row.status === 'DSR_PENDING_APP').length,
    open: rows.filter(row => row.status === 'DSR_INVESTIGATION').length,
    tabs: {
      RM: count('RM', 'DSR_PENDING_APP') + count('RM', 'DSR_INVESTIGATION'),
      FG: count('FG', 'DSR_PENDING_APP') + count('FG', 'DSR_INVESTIGATION')
    },
    pending_all: count('RM', 'DSR_PENDING_APP') + count('FG', 'DSR_PENDING_APP'),
    window: dscWindow(wh),
    escalation_enabled: slaEnabled(`DSC_ESCALATE_${wh}`),
    sessions: [
      ...new Map(rows.filter(row => row.session_no).map(row => [row.session_id, row.session_no])).entries()
    ].map(([id, no]) => ({ id, no }))
  }
}

type TraceEvent = { at: string; kind: string; title: string; detail: string; by: string }

function traceOf(disc: LocalRow): TraceEvent[] {
  const code = String(disc.scanned_code ?? '')
  const events: TraceEvent[] = []
  const unit = disc.unit_id ? findRow('rm_unit', idOf(disc.unit_id)) : undefined
  const label = disc.label_id ? findRow('fg_label', idOf(disc.label_id)) : undefined
  if (unit)
    events.push({
      at: wibStamp(String(unit.date_created)),
      kind: 'arrival',
      title: 'Barang datang di gudang',
      detail: `Stiker ${code} terbit · batch ${batchOfQr(code)} · ${bigQty(unit)} ${String(unit.unit_big_snapshot ?? '')} sekarang`,
      by: ''
    })
  if (label)
    events.push({
      at: wibStamp(String(label.date_created)),
      kind: 'arrival',
      title: 'Label diterbitkan',
      detail: `Label ${String(label.level)} ${code} · batch ${batchNoOf(label.batch_id)}`,
      by: ''
    })
  if (unit) {
    readTable('rm_putaway_task_item')
      .filter(item => idOf(item.unit_id) === unit.id)
      .forEach(item => {
        const task = findRow('rm_putaway_task', idOf(item.task_id))
        events.push({
          at: wibStamp(String(item.ts ?? item.date_updated ?? item.date_created)),
          kind: 'move',
          title: 'Putaway',
          detail: `Lokasi tujuan ${binCode(item.actual_rack_bin_id ?? item.recommended_rack_bin_id) || '—'}${task?.pwr_no ? ` · ${String(task.pwr_no)}` : ''}`,
          by: userName(task?.operator_id ?? item.user_updated)
        })
      })
    readTable('picking_task_item')
      .filter(item => idOf(item.unit_id) === unit.id)
      .forEach(item => {
        const task = findRow('picking_task', idOf(item.task_id))
        events.push({
          at: wibStamp(String(item.picked_at ?? item.date_updated ?? item.date_created)),
          kind: 'tx',
          title: `Picking — ${String(item.status ?? '')}`,
          detail: `${String(task?.pick_no ?? '')} · ${num(item.qty_taken ?? item.qty_assigned)} ${String(unit.unit_big_snapshot ?? '')}`,
          by: userName(task?.operator_id)
        })
      })
  }
  readTable('tx_log')
    .filter(row => code && (row.doc_ref === code || String(row.detail ?? '').includes(code)))
    .forEach(row =>
      events.push({
        at: wibStamp(String(row.ts)),
        kind: String(row.page_code ?? '').startsWith('IRM-0') ? 'qc' : 'tx',
        title: `${String(row.log_type)} — ${String(row.action)}`,
        detail: `${String(row.detail ?? '')}${row.page_code ? ` · ${String(row.page_code)}` : ''}`,
        by: userName(row.user_id)
      })
    )
  readTable('stock_movement')
    .filter(row => (unit && idOf(row.unit_id) === unit.id) || (label && idOf(row.label_id) === label.id))
    .forEach(row =>
      events.push({
        at: wibStamp(String(row.ts)),
        kind: 'move',
        title: `Mutasi stok ${String(row.movement_type)}`,
        detail: `${String(row.doc_ref ?? '')}${row.to_rack_bin_id ? ` → ${binCode(row.to_rack_bin_id)}` : ''}`,
        by: userName(row.user_id)
      })
    )
  logOf(disc).forEach((entry: OpnLog) =>
    events.push({ at: entry.at, kind: 'disc', title: 'Selisih', detail: entry.ev, by: userName(entry.by) })
  )
  const seen = new Set<string>()
  return events
    .filter(event => {
      const key = `${event.at}|${event.detail}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .sort((a, b) => a.at.localeCompare(b.at))
}

export function discrepancy(params: { id?: string } | undefined) {
  sweepDiscrepancies()
  const disc = findRow('discrepancy', String(params?.id ?? ''))
  if (!disc) return null
  const row = discRow(disc)
  const wh = row.warehouse
  const code = row.status
  const unit = disc.unit_id ? findRow('rm_unit', idOf(disc.unit_id)) : undefined
  const label = disc.label_id ? findRow('fg_label', idOf(disc.label_id)) : undefined
  const nowState = unit
    ? `${String(unit.unit_status)} · penanda ${String(unit.flag ?? 'REGULAR')} · lokasi ${binCode(unit.rack_bin_id) || '—'} · gudang WH-${wh}`
    : label
      ? `${String(label.label_status)} · lokasi ${labelLocation(label) || '—'} · gudang WH-FG`
      : disc.pack_stock_id
        ? `Pack lepas di ${row.location}`
        : 'Kode tidak terdaftar di master'
  return {
    ...row,
    batch: unit ? batchOfQr(unit.qr_code) : label ? batchNoOf(label.batch_id) : '',
    age_days: dayDiff(String(disc.date_created)),
    recorded_at: wibStamp(String(disc.date_created)),
    qty_system: disc.qty_system == null ? null : num(disc.qty_system),
    qty_physical: disc.qty_physical == null ? null : num(disc.qty_physical),
    unit_state: nowState,
    note: (disc.note as string | null) ?? null,
    reject_reason: (disc.reject_reason as string | null) ?? null,
    approver: disc.approver_id ? userName(disc.approver_id) : null,
    kadiv_label: KADIV_LABEL[wh],
    operators: operatorsOf(wh),
    trace: traceOf(disc),
    can: {
      escalate: code === 'DSR_INVESTIGATION' && hasRole(wh, 'admin'),
      recheck: code === 'DSR_INVESTIGATION' && hasRole(wh, 'admin'),
      found: code === 'DSR_INVESTIGATION' && hasRole(wh, 'operator'),
      decide: code === 'DSR_PENDING_APP' && hasRole(wh, 'kadiv')
    }
  }
}

/* ============================================================ aksi */

const whOfDisc = (disc: LocalRow): Wh => whOf(disc.warehouse_id)

export function escalate(body: { id?: string }) {
  const disc = discOf(body.id)
  const wh = whOfDisc(disc)
  requireWhRole(wh, 'admin', `Eskalasi selisih hanya untuk Admin WH ${wh}.`)
  dsrFlow.run(disc, 'ACT_DSR_ESCALATE_WO', actor(wh, 'ADMIN'))
  updateRow('discrepancy', String(disc.id), {
    escalated_at: iso(),
    log: withLog(disc, `Eskalasi manual ke ${KADIV_LABEL[wh]}`),
    ...auditFieldsForUpdate()
  })
  txLogWh(wh, 'OPN-04A', 'disc-escalate', String(disc.dsc_no), `Eskalasi manual ke ${KADIV_LABEL[wh]}`)
  return { id: disc.id }
}

export function recheck(body: { id?: string; operator_id?: string; note?: string }) {
  const disc = discOf(body.id)
  const wh = whOfDisc(disc)
  requireWhRole(wh, 'admin', `Re-check fisik ditugaskan oleh Admin WH ${wh}.`)
  if (statusCodeOf(disc) !== 'DSR_INVESTIGATION')
    fail('Re-check hanya untuk selisih yang masih Open.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const operator = String(body.operator_id ?? '')
  const isOperator = readTable('mt_privileges').some(
    row => idOf(row.user) === operator && findRow('mt_roles', idOf(row.role))?.code === ROLE.operator[wh]
  )
  if (!isOperator) fail(`Pilih Operator WH ${wh}.`)
  const note = String(body.note ?? '').trim()
  updateRow('discrepancy', String(disc.id), {
    recheck_operator_id: operator,
    recheck_note: note || null,
    log: withLog(disc, `Re-check fisik ditugaskan ke ${userName(operator)}${note ? ` — ${note}` : ''}`),
    ...auditFieldsForUpdate()
  })
  txLogWh(wh, 'OPN-04A', 'disc-recheck', String(disc.dsc_no), `Re-check fisik → ${userName(operator)}`)
  return { id: disc.id }
}

/** Penyesuaian stok resmi saat write-off disetujui. */
function applyWriteOff(disc: LocalRow, wh: Wh) {
  const ref = String(disc.dsc_no)
  const type = String(disc.disc_type)
  const qty = num(disc.qty)
  if (disc.unit_id) {
    const unit = findRow('rm_unit', idOf(disc.unit_id)) as LocalRow | undefined
    if (!unit) return
    const perBig = perBigOf(unit)
    if (type === 'MISSING') {
      movement('OPN_WRITE_OFF', { unit_id: unit.id }, -num(unit.qty_small), ref, { from_rack_bin_id: unit.rack_bin_id })
      updateRow('rm_unit', String(unit.id), {
        qty_small: 0,
        unit_status: 'ADJUSTED_NOT_FOUND',
        rack_bin_id: null,
        ...auditFieldsForUpdate()
      })
    } else if (type === 'QTY_LESS' || type === 'QTY_MORE') {
      const delta = (type === 'QTY_LESS' ? -1 : 1) * qty * perBig
      movement('OPN_WRITE_OFF', { unit_id: unit.id }, delta, ref, { from_rack_bin_id: unit.rack_bin_id })
      updateRow('rm_unit', String(unit.id), {
        qty_small: Math.max(0, num(unit.qty_small) + delta),
        ...auditFieldsForUpdate()
      })
    }
    return
  }
  if (disc.label_id) {
    const label = findRow('fg_label', idOf(disc.label_id)) as LocalRow | undefined
    if (!label) return
    movement('OPN_WRITE_OFF', { label_id: label.id }, -1, ref, {
      from_pallet_id: label.pallet_id ?? null,
      from_container_id: label.container_id ?? null
    })
    updateRow('fg_label', String(label.id), {
      label_status: 'ADJUSTED_NOT_FOUND',
      pallet_id: null,
      container_id: null,
      ...auditFieldsForUpdate()
    })
    return
  }
  if (disc.pack_stock_id) {
    const pack = findRow('fg_pack_stock', idOf(disc.pack_stock_id)) as LocalRow | undefined
    if (!pack) return
    const delta = type === 'PACK_LESS' ? -qty : qty
    movement('OPN_WRITE_OFF', {}, delta, ref, { from_container_id: pack.container_id ?? null })
    updateRow('fg_pack_stock', String(pack.id), {
      qty_pack: Math.max(0, num(pack.qty_pack) + delta),
      ...auditFieldsForUpdate()
    })
  }
  void wh
}

export function approve(body: { id?: string; note?: string; confirm?: boolean }) {
  const disc = discOf(body.id)
  const wh = whOfDisc(disc)
  requireWhRole(wh, 'kadiv', `Write-off ${String(disc.dsc_no)} milik ${KADIV_LABEL[wh]}.`)
  if (!body.confirm) fail('Centang konfirmasi dulu.')
  const note = String(body.note ?? '').trim()
  dsrFlow.run(disc, 'ACT_DSR_APP_WRITEOFF', actor(wh, 'KADIV'), {}, note)
  applyWriteOff(disc, wh)
  updateRow('discrepancy', String(disc.id), {
    approver_id: currentUserId(),
    approved_at: iso(),
    resolved_at: iso(),
    resolved_by_id: currentUserId(),
    note: note || null,
    log: withLog(disc, `Write-off disetujui — Adjusted - Not Found${note ? ` — ${note}` : ''}`),
    ...auditFieldsForUpdate()
  })
  txLogWh(
    wh,
    'OPN-04A',
    'disc-writeoff',
    String(disc.scanned_code || disc.dsc_no),
    `Write-off ${String(disc.dsc_no)} disetujui ${KADIV_LABEL[wh]} — ${num(disc.qty)} ${String(disc.unit_name ?? '')} dikurangi resmi`
  )
  return { id: disc.id }
}

export function reject(body: { id?: string; reason?: string; days?: number }) {
  const disc = discOf(body.id)
  const wh = whOfDisc(disc)
  requireWhRole(wh, 'kadiv', `Keputusan write-off milik ${KADIV_LABEL[wh]}.`)
  const reason = String(body.reason ?? '').trim()
  const days = Math.floor(num(body.days))
  if (!reason || !(days >= 1)) fail('Alasan & jumlah hari wajib diisi.')
  dsrFlow.run(disc, 'ACT_DSR_REJ_WRITEOFF', actor(wh, 'KADIV'), {}, reason)
  const now = iso()
  updateRow('discrepancy', String(disc.id), {
    window_due_at: dueEndOfDay(now, days),
    approval_due_at: dueEndOfDay(now, days + (dscWindow(wh)[1] - dscWindow(wh)[0])),
    escalated_at: null,
    reject_reason: reason,
    log: withLog(disc, `Reject → Open, window +${days} hari — ${reason}`),
    ...auditFieldsForUpdate()
  })
  txLogWh(
    wh,
    'OPN-04A',
    'disc-reject',
    String(disc.dsc_no),
    `Reject write-off — kembali Open, window +${days} hari: ${reason}`
  )
  return { id: disc.id, days }
}

/** "Ditemukan" — hanya lewat scan OPN-03 (stiker discan sebagai bukti fisik, lalu qty yang ditemukan). */
export function found(body: { id?: string; code?: string; qty?: number }) {
  const disc = discOf(body.id)
  const wh = whOfDisc(disc)
  requireWhRole(wh, 'operator', `Konfirmasi ditemukan dilakukan Operator WH ${wh} lewat scan.`)
  if (statusCodeOf(disc) !== 'DSR_INVESTIGATION')
    fail('Selisih ini sudah tidak Open.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (!disc.pack_stock_id && up(body.code) !== up(disc.scanned_code))
    fail('Kode tidak cocok dengan discrepancy ini.', 'ERR_CODE_MISMATCH')
  const total = num(disc.qty)
  const qty = num(body.qty)
  if (!(qty >= 1)) fail('Isi jumlah yang ditemukan dulu.')
  if (qty > total) fail(`Melebihi selisih yang tercatat (${total})`)
  const partial = qty < total
  if (partial) {
    const child = insertRow('discrepancy', {
      ...disc,
      ...auditFieldsForCreate(),
      id: newId(),
      dsc_no: `${String(disc.dsc_no)}-F`,
      qty,
      parent_id: disc.id,
      submission_id: null,
      log: [
        {
          at: wibStamp(),
          ev: `Ditemukan sebagian ${qty} ${String(disc.unit_name ?? '')} dari ${String(disc.dsc_no)}`,
          by: currentUserId()
        }
      ]
    })
    const { statusId, submissionId } = dsrFlow.start(String(child.id))
    updateRow('discrepancy', String(child.id), { status_id: statusId, submission_id: submissionId })
    dsrFlow.run(findRow('discrepancy', String(child.id)) as LocalRow, 'ACT_DSR_SCAN_FOUND', actor(wh, 'OP'))
    updateRow('discrepancy', String(child.id), {
      found_qty: qty,
      resolved_at: iso(),
      resolved_by_id: currentUserId()
    })
    updateRow('discrepancy', String(disc.id), {
      qty: round3(total - qty),
      log: withLog(disc, `Ditemukan sebagian ${qty} — sisa ${round3(total - qty)} tetap Investigation`),
      ...auditFieldsForUpdate()
    })
  } else {
    dsrFlow.run(disc, 'ACT_DSR_SCAN_FOUND', actor(wh, 'OP'))
    updateRow('discrepancy', String(disc.id), {
      found_qty: qty,
      resolved_at: iso(),
      resolved_by_id: currentUserId(),
      log: withLog(disc, `Dikonfirmasi ditemukan ${qty} ${String(disc.unit_name ?? '')} (scan OPN-03)`),
      ...auditFieldsForUpdate()
    })
    /* Unit/label yang dikeluarkan dari stok saat picking ("Hilang") kembali ke tempatnya. */
    const unit = disc.unit_id ? findRow('rm_unit', idOf(disc.unit_id)) : undefined
    if (unit && unit.unit_status === 'MISSING')
      updateRow('rm_unit', String(unit.id), { unit_status: 'IN_STOCK', ...auditFieldsForUpdate() })
    const label = disc.label_id ? findRow('fg_label', idOf(disc.label_id)) : undefined
    if (label && label.label_status === 'MISSING')
      updateRow('fg_label', String(label.id), { label_status: 'IN_STOCK', ...auditFieldsForUpdate() })
  }
  txLogWh(
    wh,
    'OPN-03',
    'opn-found-confirm',
    String(disc.scanned_code || disc.dsc_no),
    `Konfirmasi ditemukan ${qty}${partial ? ` (sebagian — sisa ${round3(total - qty)} tetap Investigation)` : ''} · ${String(disc.dsc_no)}`
  )
  return { id: disc.id, partial, remaining: partial ? round3(total - qty) : 0 }
}

/** Tugas re-check untuk Operator (OPN-03): selisih Open yang ditunjuk ke saya. */
export function myRechecks() {
  const me = currentUserId()
  return readTable('discrepancy')
    .filter(disc => idOf(disc.recheck_operator_id) === me && statusCodeOf(disc) === 'DSR_INVESTIGATION')
    .map(disc => ({ ...discRow(disc), note: (disc.recheck_note as string | null) ?? null }))
}

/** Hitungan selisih aktif per gudang (kartu INV-03 "Selisih … dalam investigasi"). */
export function activeDiscCount(wh: Wh) {
  sweepDiscrepancies()
  return readTable('discrepancy').filter(
    disc => whOf(disc.warehouse_id) === wh && !TERMINAL_DSR.includes(statusCodeOf(disc)) && !disc.parent_id
  ).length
}
